import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { Reveal, Rise } from "./Reveal";
import content from "../content";

const GOLD = "#F0C060";

export default function HeritageFlyers() {
  const flyers = content.heritage?.flyers || [];
  const [open, setOpen] = useState(null);

  const close = useCallback(() => setOpen(null), []);
  const go = useCallback(
    (dir) => setOpen((i) => (i === null ? i : (i + dir + flyers.length) % flyers.length)),
    [flyers.length]
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close, go]);

  if (flyers.length === 0) return null;

  return (
    <div className="relative z-[3] mt-16 md:mt-20" data-testid="heritage-flyers">
      <Reveal>
        <Rise>
          <p
            className="mb-8 font-poppins text-sm md:text-base font-semibold tracking-[0.3em] uppercase"
            style={{ color: GOLD }}
          >
            Eventos
          </p>
        </Rise>
        <div className="grid gap-5 sm:grid-cols-2 max-w-3xl mx-auto">
          {flyers.map((f, i) => (
            <Rise key={f.image}>
              <button
                type="button"
                onClick={() => setOpen(i)}
                data-testid={`heritage-flyer-${i}`}
                className="group relative block w-full overflow-hidden rounded-2xl border border-white/12 bg-white/[0.04] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F0C060]"
                aria-label={`Ver ${f.title}`}
              >
                <img
                  src={f.image}
                  alt={f.title}
                  loading="lazy"
                  className="w-full aspect-square object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                <span className="pointer-events-none absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ZoomIn size={16} />
                </span>
                <span className="absolute inset-x-0 bottom-0 p-4 text-left">
                  <span className="block font-anton text-white text-lg md:text-xl uppercase leading-tight">
                    {f.title}
                  </span>
                  {f.subtitle && (
                    <span className="mt-0.5 block font-poppins text-ceramic/85 text-xs md:text-sm">
                      {f.subtitle}
                    </span>
                  )}
                </span>
              </button>
            </Rise>
          ))}
        </div>
      </Reveal>

      <AnimatePresence>
        {open !== null && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            data-testid="heritage-lightbox"
          >
            <button
              onClick={close}
              aria-label="Cerrar"
              data-testid="lightbox-close"
              className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
            >
              <X size={22} />
            </button>
            {flyers.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); go(-1); }}
                  aria-label="Anterior"
                  data-testid="lightbox-prev"
                  className="absolute left-3 md:left-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <ChevronLeft size={26} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); go(1); }}
                  aria-label="Siguiente"
                  data-testid="lightbox-next"
                  className="absolute right-3 md:right-6 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <ChevronRight size={26} />
                </button>
              </>
            )}
            <motion.img
              key={flyers[open].image}
              src={flyers[open].image}
              alt={flyers[open].title}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[86vh] max-w-[92vw] rounded-2xl shadow-2xl object-contain"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
